/**
 * AppendixExplainer Component
 *
 * Appendix page content: "Why system layout and pipe sizing matter"
 *
 * Uses the motorway analogy WITHOUT any brand names:
 * - "aggressive drivers" instead of specific car brands
 *
 * Mappings:
 * - Built too small → undersized primaries / too many small branches
 * - Speed cameras → modulation (boiler output control)
 * - Improved junctions → S-plan (separated flow paths)
 * - Extra lanes → larger primaries (28mm)
 * - Variable speed limits → adaptive temperature control
 * - Sludge → restriction / congestion
 *
 * Includes the engineering clue line about cold radiators.
 */

import './AppendixExplainer.css';

interface AppendixExplainerProps {
  /** Print mode (simplified styling) */
  printMode?: boolean;
  /** Whether to show in compact story mode */
  storyMode?: boolean;
  /** Optional class name */
  className?: string;
}

export function AppendixExplainer({
  printMode = false,
  storyMode = false,
  className = '',
}: AppendixExplainerProps) {
  return (
    <div
      className={`appendix-explainer ${printMode ? 'appendix-explainer--print' : ''} ${storyMode ? 'appendix-explainer--story' : ''} ${className}`}
    >
      <div className="explainer-header">
        <h2 className="explainer-title">Why system layout and pipe sizing matter</h2>
        <p className="explainer-intro">
          Your heating system is like a road network. Just as traffic flow depends on road width,
          junctions, and congestion, your heating performance depends on pipe sizes, system layout,
          and water quality.
        </p>
      </div>

      {/* The Motorway Analogy */}
      <section className="explainer-section">
        <h3 className="section-title">The motorway analogy</h3>

        <div className="analogy-grid">
          {/* Undersized primaries */}
          <div className="analogy-card">
            <div className="analogy-icon">🛣️</div>
            <div className="analogy-content">
              <h4 className="analogy-label">Built too small</h4>
              <p className="analogy-road">A busy motorway with only two lanes</p>
              <p className="analogy-heating">
                <strong>In heating:</strong> Undersized primary pipes or too many small branches.
                The boiler has capacity, but the water can't flow fast enough.
              </p>
            </div>
          </div>

          {/* Modulation */}
          <div className="analogy-card">
            <div className="analogy-icon">📷</div>
            <div className="analogy-content">
              <h4 className="analogy-label">Speed cameras</h4>
              <p className="analogy-road">Cameras that regulate traffic speed smoothly</p>
              <p className="analogy-heating">
                <strong>In heating:</strong> Modulation — the boiler varying its output to match
                actual demand. Reduces cycling and improves efficiency.
              </p>
            </div>
          </div>

          {/* S-plan */}
          <div className="analogy-card">
            <div className="analogy-icon">🔀</div>
            <div className="analogy-content">
              <h4 className="analogy-label">Improved junctions</h4>
              <p className="analogy-road">Smart interchanges that separate traffic flows</p>
              <p className="analogy-heating">
                <strong>In heating:</strong> S-plan layout — separate motorised valves for heating
                and hot water circuits. Better control, less interference.
              </p>
            </div>
          </div>

          {/* Larger primaries */}
          <div className="analogy-card">
            <div className="analogy-icon">🚗</div>
            <div className="analogy-content">
              <h4 className="analogy-label">Extra lanes</h4>
              <p className="analogy-road">Widening from three lanes to four</p>
              <p className="analogy-heating">
                <strong>In heating:</strong> Larger primary pipes (28mm vs 22mm). More flow
                capacity, especially important for heat pumps at lower temperatures.
              </p>
            </div>
          </div>

          {/* Adaptive controls */}
          <div className="analogy-card">
            <div className="analogy-icon">🚦</div>
            <div className="analogy-content">
              <h4 className="analogy-label">Variable speed limits</h4>
              <p className="analogy-road">Smart motorways that adjust limits based on conditions</p>
              <p className="analogy-heating">
                <strong>In heating:</strong> Adaptive temperature control — adjusting flow
                temperature based on weather and demand. Saves energy, improves comfort.
              </p>
            </div>
          </div>

          {/* Sludge/congestion */}
          <div className="analogy-card">
            <div className="analogy-icon">🚧</div>
            <div className="analogy-content">
              <h4 className="analogy-label">Roadworks and congestion</h4>
              <p className="analogy-road">Debris and restrictions slowing traffic</p>
              <p className="analogy-heating">
                <strong>In heating:</strong> Sludge and magnetite buildup. Restricts flow, reduces
                efficiency, can damage pumps and valves over time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why aggressive drivers matter */}
      <section className="explainer-section">
        <h3 className="section-title">Why "aggressive driving" causes problems</h3>
        <p className="section-text">
          Some boilers are like aggressive drivers — they respond quickly and powerfully to any
          demand. On a well-designed road network, this is fine. But on a restricted network (small
          pipes, poor layout), aggressive behaviour causes:
        </p>
        <ul className="explainer-list">
          <li>
            <strong>Short cycling</strong> — the boiler fires, reaches temperature quickly, shuts
            off, then fires again moments later
          </li>
          <li>
            <strong>Pump overrun issues</strong> — the boiler overheats because water isn't
            circulating fast enough
          </li>
          <li>
            <strong>Uneven heating</strong> — some radiators get too hot while others barely warm up
          </li>
          <li>
            <strong>Higher fuel bills</strong> — cycling is inefficient; standing losses increase
          </li>
        </ul>
        <p className="section-text">
          Modern modulating boilers (the "speed camera" approach) solve this by varying output to
          match the network capacity and actual demand.
        </p>
      </section>

      {/* Engineering clue */}
      <section className="explainer-section explainer-clue">
        <div className="clue-icon">💡</div>
        <div className="clue-content">
          <h4 className="clue-title">The engineering clue</h4>
          <p className="clue-text">
            <em>
              "A common symptom is: 'the radiator at the far end never gets properly hot' — this
              often indicates distribution limits rather than distance alone."
            </em>
          </p>
          <p className="clue-explanation">
            If your furthest radiators are cold while near ones are hot, it's usually not about
            distance. It's about hydraulic resistance: the water takes the path of least resistance.
            Solutions include balancing (restricting flow to nearby radiators), fitting larger
            pipes, or switching to an S-plan layout.
          </p>
        </div>
      </section>

      {/* Heat pump implications */}
      <section className="explainer-section">
        <h3 className="section-title">Why this matters even more for heat pumps</h3>
        <p className="section-text">
          Heat pumps deliver heat at lower temperatures (35-45°C vs 60-75°C for gas boilers). This
          means:
        </p>
        <ul className="explainer-list">
          <li>
            <strong>Higher flow rates needed</strong> — to deliver the same heat, more water must
            circulate
          </li>
          <li>
            <strong>Distribution limits exposed</strong> — restrictions that were acceptable with
            hotter water become bottlenecks
          </li>
          <li>
            <strong>Larger emitters required</strong> — radiators sized for 70°C water may not heat
            the room at 40°C
          </li>
        </ul>
        <p className="section-text">
          This is why we show heat pump scenarios as "as-is" vs "low-temp compatible" — the
          distribution system can make a 20-30% difference in running costs.
        </p>
      </section>

      {/* Summary table */}
      <section className="explainer-section">
        <h3 className="section-title">Quick reference</h3>
        <table className="explainer-table">
          <thead>
            <tr>
              <th>Road analogy</th>
              <th>Heating equivalent</th>
              <th>Solution</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Two-lane motorway</td>
              <td>Undersized primary pipes</td>
              <td>Upsize to 28mm where possible</td>
            </tr>
            <tr>
              <td>Single junction</td>
              <td>Y-plan (shared valve)</td>
              <td>Convert to S-plan for better control</td>
            </tr>
            <tr>
              <td>Aggressive drivers</td>
              <td>On/off boiler control</td>
              <td>Modulating boiler + smart controls</td>
            </tr>
            <tr>
              <td>Roadworks</td>
              <td>Sludge/contamination</td>
              <td>Powerflush + inhibitor</td>
            </tr>
            <tr>
              <td>Fixed speed limit</td>
              <td>Fixed flow temperature</td>
              <td>Weather compensation</td>
            </tr>
          </tbody>
        </table>
      </section>

      <p className="explainer-footer">
        Understanding your distribution system helps you make informed decisions about upgrades —
        and explains why sometimes a "better" boiler won't solve the underlying problem.
      </p>
    </div>
  );
}

export default AppendixExplainer;
